// First funnel-level A/B test: full /2026 funnel (control) vs. /detox funnel
// (treatment).
//
// Hypothesis:
//   The /detox funnel — tier classification, six-card "toolkit" sneak peek,
//   New Yorker press citation, 7-day trial, "Start your phone detox" hero —
//   converts better Welcome → Checkout Completed than the legacy /2026 funnel
//   for the Meta-ad audience this campaign targets (50-65+ women).
//
// Primary metric (analyze in Amplitude):
//   Funnel: `Web Funnel - Welcome Viewed` → `Web Funnel - Checkout Completed`
//   grouped by `event_properties.experiments.funnel_detox_vs_2026`
//
// Secondary metrics worth watching:
//   - Welcome Viewed → Paywall Viewed     (qualified-paywall rate)
//   - Paywall Viewed → Plan Selected      (price page engagement)
//   - Paywall Viewed → Checkout Completed (close rate)
//   - Welcome Viewed → Email Submitted    (lead-capture rate)
//
// Targeting:
//   Enforced at REDIRECT TIME in src/app.jsx. Only users who land on /2026
//   hit `getVariant()`; direct visits to /detox stay out of the experiment
//   so QA/preview links don't pollute exposure data. Treatment users are
//   redirected to /detox via `location.replace()` before any Welcome Viewed
//   event fires, so attribution is clean.
//
// Rollout plan:
//   Stage 1: Flip Running at 50/50 with 5% rollout in Amplitude UI — smoke
//            test exposure pipeline + variant tag on a handful of users.
//   Stage 2: Bump to 100% rollout — primary read. Min ~2000 Welcome Viewed
//            per variant for a meaningful conversion delta.
//   Stage 3: Roll out winner to 100% in Amplitude UI; retire loser branch
//            in a follow-up cleanup PR (after ~2 weeks of stable data).
//
// QA overrides:
//   ?exp_override=funnel_detox_vs_2026:detox-funnel  → forces /detox redirect
//   ?exp_override=funnel_detox_vs_2026:2026-funnel   → forces /2026 stay
//   Overrides skip exposure firing, so QA traffic doesn't pollute analysis.
//
// Direct preview paths (out-of-experiment):
//   /detox   — see the treatment funnel without firing exposure
//   /2026    — see the experiment in action (~50/50 redirect)

import { defineExperiment } from '../src/lib/experiments/define';

export default defineExperiment({
  key: 'funnel_detox_vs_2026',
  name: 'Funnel — /detox vs /2026',
  description:
    'Tests the new /detox funnel against the existing /2026 funnel. Users land on /2026; treatment users are redirected to /detox before any Welcome Viewed fires. Persisted client-side via amplitude_id bucketing.',
  evaluationMode: 'local',
  bucketingKey: 'amplitude_id',
  bucketingUnit: 'User',
  // Variant keys match the live Amplitude flag config exactly — the redirect
  // in src/app.jsx compares the resolved value against `'detox-funnel'`. Do
  // NOT rename these to generic control/treatment without renaming the
  // Amplitude variants in lockstep, or the redirect silently stops firing
  // (the bug this definition was corrected to fix).
  // Variant descriptions mirror the live Amplitude config EXACTLY (no "Control"/
  // "Treatment" prefixes). The Management API does not support patching variants,
  // so any drift here fails the sync — keep these byte-for-byte in step with
  // Amplitude. See the conclusion note on rolloutWeights below.
  variants: [
    { key: '2026-funnel',  description: 'Stays on /2026 (legacy screentime detox funnel)' },
    { key: 'detox-funnel', description: 'Redirects to /detox (new Social Media Detox Plan funnel)' },
  ],
  // Experiment concluded 2026-06-05 — decision: rollout, winner: 2026-funnel
  // (control). Rolled out to 100/0 in the Amplitude UI. Weights mirror that
  // post-rollout state so the sync sees no unpatchable drift (same handling as
  // welcome_2026_headline_v1). The /detox redirect in src/app.jsx is now inert
  // for live traffic (treatment weight 0); this surface can be retired in a
  // follow-up cleanup PR.
  rolloutWeights: { '2026-funnel': 100, 'detox-funnel': 0 },
  mutualExclusionGroup: 'global',
  // targetSegments intentionally omitted — local-eval rejects user-property
  // segment shapes. Scoping is enforced at redirect time (only /2026 calls
  // getVariant; /detox direct visits stay out of the experiment).
  experimentType: 'a-b-test',
  tags: ['2026', 'funnel', 'detox'],
  primaryMetric: 'welcome_viewed_to_checkout_completed',
});
